import { useCallback, useEffect, useMemo, useState } from 'react';
import AdminPageShell from './AdminPageShell';
import AdminToast from './AdminToast';
import ApprenantPageShell from '../apprenant/ApprenantPageShell';
import UserProfileAvatar from '../common/UserProfileAvatar';
import AcademiaSelect from '../common/AcademiaSelect';
import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import { authService } from '../../services/authService';

type AdminUser = {
  id?: string;
  telephone?: string;
  email?: string;
  role?: string;
  profil?: {
    prenom?: string;
    nom?: string;
    postnom?: string | null;
    sexe?: 'M' | 'F' | null;
    dateNaissance?: string | Date | null;
    photoUrl?: string | null;
  };
};

interface AdminProfilePanelProps {
  user: AdminUser;
  onUserUpdated?: (user: AdminUser) => void;
  /** `apprenant` : hero espace apprenant (fond banner 2). */
  variant?: 'admin' | 'apprenant';
}

type ToastState = { message: string; variant: 'success' | 'error' } | null;

function toDateInputValue(raw: string | Date | null | undefined) {
  if (!raw) {
    return '';
  }
  const d = raw instanceof Date ? raw : new Date(raw);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  return d.toISOString().slice(0, 10);
}

export default function AdminProfilePanel({ user, onUserUpdated, variant = 'admin' }: AdminProfilePanelProps) {
  const isApprenant = variant === 'apprenant';
  const PageShell = isApprenant ? ApprenantPageShell : AdminPageShell;
  const profile = user?.profil ?? {};
  const [prenom, setPrenom] = useState(profile.prenom ?? '');
  const [nom, setNom] = useState(profile.nom ?? '');
  const [postnom, setPostnom] = useState(profile.postnom ?? '');
  const [sexe, setSexe] = useState(profile.sexe ?? '');
  const [dateNaissance, setDateNaissance] = useState(toDateInputValue(profile.dateNaissance));
  const [photoUrl, setPhotoUrl] = useState(profile.photoUrl ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [ancienMotdepasse, setAncienMotdepasse] = useState('');
  const [nouveauMotdepasse, setNouveauMotdepasse] = useState('');
  const [confirmMotdepasse, setConfirmMotdepasse] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [toast, setToast] = useState<ToastState>(null);

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToast({ message, variant });
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);

  useEffect(() => {
    setPrenom(profile.prenom ?? '');
    setNom(profile.nom ?? '');
    setPostnom(profile.postnom ?? '');
    setSexe(profile.sexe ?? '');
    setDateNaissance(toDateInputValue(profile.dateNaissance));
    setPhotoUrl(profile.photoUrl ?? '');
  }, [profile.dateNaissance, profile.nom, profile.photoUrl, profile.postnom, profile.prenom, profile.sexe]);

  const initials = useMemo(() => {
    const p = (prenom || '').trim().slice(0, 1);
    const n = (nom || '').trim().slice(0, 1);
    return `${p}${n}`.toUpperCase() || 'AD';
  }, [prenom, nom]);

  const handleUploadImage = async (file?: File | null) => {
    if (!file || isUploading) {
      return;
    }
    setIsUploading(true);
    setToast(null);
    try {
      const uploadedUrl = await authService.uploadProfileImage(file);
      setPhotoUrl(uploadedUrl);
      const updated = await authService.updateProfile({ photoUrl: uploadedUrl });
      onUserUpdated?.(updated as AdminUser);
      showToast('Photo de profil mise à jour.', 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Téléversement impossible.', 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = async () => {
    if (isSaving) {
      return;
    }
    setIsSaving(true);
    setToast(null);
    try {
      const updated = await authService.updateProfile({
        prenom: prenom.trim(),
        nom: nom.trim(),
        postnom: postnom.trim() || null,
        sexe: sexe === 'M' || sexe === 'F' ? sexe : null,
        dateNaissance: dateNaissance || null,
        photoUrl: photoUrl.trim() || null,
      });
      onUserUpdated?.(updated as AdminUser);
      showToast(
        isApprenant
          ? 'Profil mis à jour.'
          : user.role === 'MODERATEUR'
            ? 'Profil modérateur mis à jour.'
            : 'Profil administrateur mis à jour.',
        'success',
      );
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Mise à jour impossible.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (isChangingPassword) {
      return;
    }
    setToast(null);

    if (!ancienMotdepasse || !nouveauMotdepasse || !confirmMotdepasse) {
      showToast('Tous les champs mot de passe sont requis.', 'error');
      return;
    }
    if (nouveauMotdepasse.length < 8) {
      showToast('Le nouveau mot de passe doit contenir au moins 8 caractères.', 'error');
      return;
    }
    if (nouveauMotdepasse !== confirmMotdepasse) {
      showToast('La confirmation ne correspond pas au nouveau mot de passe.', 'error');
      return;
    }

    setIsChangingPassword(true);
    try {
      await authService.changePassword({
        ancienMotdepasse,
        nouveauMotdepasse,
        confirmMotdepasse,
      });
      showToast('Mot de passe mis à jour avec succès.', 'success');
      setAncienMotdepasse('');
      setNouveauMotdepasse('');
      setConfirmMotdepasse('');
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Changement de mot de passe impossible.', 'error');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const displayName = [prenom, nom].filter(Boolean).join(' ') || user.telephone || (isApprenant ? 'Apprenant' : 'Administrateur');

  const roleLabel = isApprenant
    ? 'Apprenant'
    : user.role === 'MODERATEUR'
      ? 'Modérateur'
      : user.role || 'ADMINISTRATEUR';

  const apprenantContent = (
    <div className="apprenant-profil">
      <div className="apprenant-profil__grid">
        <section className="apprenant-profil-panel" aria-labelledby="apprenant-profil-identity-heading">
          <header className="apprenant-profil-panel__header">
            <span className="apprenant-profil-panel__header-icon" aria-hidden>
              <LucideSymbolIcon name="person" tone="inherit" size={22} />
            </span>
            <div className="min-w-0">
              <h2 id="apprenant-profil-identity-heading" className="apprenant-profil-panel__title">
                Identité
              </h2>
              <p className="apprenant-profil-panel__subtitle">Photo et coordonnées de connexion</p>
            </div>
          </header>
          <div className="apprenant-profil-panel__body">
            <div className="apprenant-profil-identity__hero">
              <UserProfileAvatar
                name={displayName}
                prenom={prenom}
                nom={nom}
                photoUrl={photoUrl || null}
                size="md"
              />
              <div className="min-w-0 flex-1">
                <p className="apprenant-profil-identity__name">{displayName}</p>
                {postnom ? (
                  <p className="apprenant-profil-identity__postnom">{postnom}</p>
                ) : null}
                <div className="apprenant-profil-identity__photo-actions">
                  <label className="apprenant-profil__btn apprenant-profil__btn--outline">
                    <LucideSymbolIcon name="upload" tone="inherit" size={16} />
                    {isUploading ? 'Téléversement…' : 'Changer la photo'}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(event) => void handleUploadImage(event.target.files?.[0] ?? null)}
                    />
                  </label>
                  {photoUrl ? (
                    <button
                      type="button"
                      className="apprenant-profil-identity__remove-photo"
                      onClick={() => {
                        setPhotoUrl('');
                        void (async () => {
                          try {
                            const updated = await authService.updateProfile({ photoUrl: null });
                            onUserUpdated?.(updated as AdminUser);
                            showToast('Photo de profil retirée.', 'success');
                          } catch (error) {
                            showToast(error instanceof Error ? error.message : 'Suppression impossible.', 'error');
                          }
                        })();
                      }}
                    >
                      Retirer
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
            <ul className="apprenant-profil-facts">
              <li className="apprenant-profil-fact">
                <span className="apprenant-profil-fact__icon" aria-hidden>
                  <LucideSymbolIcon name="call" tone="inherit" size={18} />
                </span>
                <div className="min-w-0">
                  <p className="apprenant-profil-fact__label">Téléphone</p>
                  <p className="apprenant-profil-fact__value">{user.telephone || 'Non renseigné'}</p>
                </div>
              </li>
              <li className="apprenant-profil-fact">
                <span className="apprenant-profil-fact__icon" aria-hidden>
                  <LucideSymbolIcon name="mail" tone="inherit" size={18} />
                </span>
                <div className="min-w-0">
                  <p className="apprenant-profil-fact__label">E-mail</p>
                  <p className="apprenant-profil-fact__value break-all">{user.email || 'Non renseigné'}</p>
                </div>
              </li>
              <li className="apprenant-profil-fact">
                <span className="apprenant-profil-fact__icon" aria-hidden>
                  <LucideSymbolIcon name="badge" tone="inherit" size={18} />
                </span>
                <div className="min-w-0">
                  <p className="apprenant-profil-fact__label">Profil</p>
                  <p className="apprenant-profil-fact__value">{roleLabel}</p>
                </div>
              </li>
            </ul>
          </div>
        </section>

        <section
          className="apprenant-profil-panel apprenant-profil-panel--wide"
          aria-labelledby="apprenant-profil-edit-heading"
        >
          <header className="apprenant-profil-panel__header">
            <span className="apprenant-profil-panel__header-icon" aria-hidden>
              <LucideSymbolIcon name="edit" tone="inherit" size={22} />
            </span>
            <div className="min-w-0">
              <h2 id="apprenant-profil-edit-heading" className="apprenant-profil-panel__title">
                Modifier mes informations
              </h2>
              <p className="apprenant-profil-panel__subtitle">
                Nom, prénom et informations personnelles affichées sur votre dossier
              </p>
            </div>
          </header>
          <div className="apprenant-profil-panel__body">
            <div className="apprenant-profil-form">
              <label className="apprenant-profil-form__field">
                <span className="apprenant-profil-form__label">Prénom</span>
                <input
                  value={prenom}
                  onChange={(e) => setPrenom(e.target.value)}
                  className="apprenant-profil-form__input"
                />
              </label>
              <label className="apprenant-profil-form__field">
                <span className="apprenant-profil-form__label">Nom</span>
                <input
                  value={nom}
                  onChange={(e) => setNom(e.target.value)}
                  className="apprenant-profil-form__input"
                />
              </label>
              <label className="apprenant-profil-form__field">
                <span className="apprenant-profil-form__label">Post-nom</span>
                <input
                  value={postnom}
                  onChange={(e) => setPostnom(e.target.value)}
                  className="apprenant-profil-form__input"
                  placeholder="Optionnel"
                />
              </label>
              <label className="apprenant-profil-form__field">
                <span className="apprenant-profil-form__label">Sexe</span>
                <AcademiaSelect value={sexe} onChange={(e) => setSexe(e.target.value)}>
                  <option value="">Non renseigné</option>
                  <option value="M">Masculin</option>
                  <option value="F">Féminin</option>
                </AcademiaSelect>
              </label>
              <label className="apprenant-profil-form__field apprenant-profil-form__field--wide">
                <span className="apprenant-profil-form__label">Date de naissance</span>
                <input
                  type="date"
                  value={dateNaissance}
                  onChange={(e) => setDateNaissance(e.target.value)}
                  className="apprenant-profil-form__input apprenant-profil-form__input--date"
                />
              </label>
            </div>

            <div className="apprenant-profil-panel__actions">
              <button
                type="button"
                onClick={() => void handleSave()}
                disabled={isSaving || isUploading}
                className="apprenant-profil__btn apprenant-profil__btn--primary"
              >
                <LucideSymbolIcon name="save" tone="inherit" size={16} className="text-white" />
                {isSaving ? 'Enregistrement…' : 'Enregistrer le profil'}
              </button>
            </div>
          </div>
        </section>
      </div>

      <section className="apprenant-profil-panel" aria-labelledby="apprenant-profil-security-heading">
        <header className="apprenant-profil-panel__header">
          <span className="apprenant-profil-panel__header-icon apprenant-profil-panel__header-icon--security" aria-hidden>
            <LucideSymbolIcon name="lock" tone="inherit" size={22} />
          </span>
          <div className="min-w-0">
            <h2 id="apprenant-profil-security-heading" className="apprenant-profil-panel__title">
              Sécurité du compte
            </h2>
            <p className="apprenant-profil-panel__subtitle">
              Modifiez votre mot de passe pour sécuriser votre accès
            </p>
          </div>
        </header>
        <div className="apprenant-profil-panel__body">
          <div className="apprenant-profil-form apprenant-profil-form--security">
            <label className="apprenant-profil-form__field">
              <span className="apprenant-profil-form__label">Mot de passe actuel</span>
              <input
                type="password"
                value={ancienMotdepasse}
                onChange={(e) => setAncienMotdepasse(e.target.value)}
                className="apprenant-profil-form__input"
                autoComplete="current-password"
              />
            </label>
            <label className="apprenant-profil-form__field">
              <span className="apprenant-profil-form__label">Nouveau mot de passe</span>
              <input
                type="password"
                value={nouveauMotdepasse}
                onChange={(e) => setNouveauMotdepasse(e.target.value)}
                className="apprenant-profil-form__input"
                autoComplete="new-password"
                placeholder="8 caractères minimum"
              />
            </label>
            <label className="apprenant-profil-form__field">
              <span className="apprenant-profil-form__label">Confirmer le nouveau mot de passe</span>
              <input
                type="password"
                value={confirmMotdepasse}
                onChange={(e) => setConfirmMotdepasse(e.target.value)}
                className="apprenant-profil-form__input"
                autoComplete="new-password"
              />
            </label>
          </div>
          <div className="apprenant-profil-panel__actions">
            <button
              type="button"
              onClick={() => void handleChangePassword()}
              disabled={isChangingPassword}
              className="apprenant-profil__btn apprenant-profil__btn--primary"
            >
              <LucideSymbolIcon name="lock" tone="inherit" size={16} className="text-white" />
              {isChangingPassword ? 'Mise à jour…' : 'Changer le mot de passe'}
            </button>
          </div>
        </div>
      </section>
    </div>
  );

  const adminContent = (
    <>
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <article className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-sm">
          <h2 className="text-sm font-black uppercase tracking-wider text-on-surface-variant">Identité</h2>
          <div className="mt-4 flex items-center gap-4">
            <UserProfileAvatar
              name={displayName}
              prenom={prenom}
              nom={nom}
              photoUrl={photoUrl || null}
              size="md"
            />
            <div className="flex-1">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-outline-variant/30 px-3 py-2 text-xs font-bold uppercase tracking-wide text-primary hover:bg-surface-container-high">
                <LucideSymbolIcon name="upload" tone="primary" size={18} className="shrink-0" />
                {isUploading ? 'Téléversement…' : 'Changer la photo'}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) => void handleUploadImage(event.target.files?.[0] ?? null)}
                />
              </label>
              <button
                type="button"
                className="mt-2 block text-xs font-semibold text-error hover:underline"
                onClick={() => {
                  setPhotoUrl('');
                  void (async () => {
                    try {
                      const updated = await authService.updateProfile({ photoUrl: null });
                      onUserUpdated?.(updated as AdminUser);
                      showToast('Photo de profil retirée.', 'success');
                    } catch (error) {
                      showToast(error instanceof Error ? error.message : 'Suppression impossible.', 'error');
                    }
                  })();
                }}
              >
                Retirer la photo
              </button>
            </div>
          </div>
          <dl className="mt-5 space-y-3 text-sm">
            <div>
              <dt className="text-on-surface-variant">Téléphone</dt>
              <dd className="font-semibold text-on-surface">{user.telephone || 'Non renseigné'}</dd>
            </div>
            <div>
              <dt className="text-on-surface-variant">Email</dt>
              <dd className="font-semibold text-on-surface">{user.email || 'Non renseigné'}</dd>
            </div>
            <div>
              <dt className="text-on-surface-variant">Rôle</dt>
              <dd className="font-semibold text-on-surface">{user.role || 'ADMINISTRATEUR'}</dd>
            </div>
          </dl>
        </article>

        <article className="xl:col-span-2 rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-sm">
          <h2 className="text-sm font-black uppercase tracking-wider text-on-surface-variant">Modifier mes informations</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="text-sm">
              <span className="mb-1 block text-on-surface-variant">Prénom</span>
              <input value={prenom} onChange={(e) => setPrenom(e.target.value)} className="w-full rounded-lg border border-outline-variant/25 bg-white px-3 py-2" />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-on-surface-variant">Nom</span>
              <input value={nom} onChange={(e) => setNom(e.target.value)} className="w-full rounded-lg border border-outline-variant/25 bg-white px-3 py-2" />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-on-surface-variant">Post-nom</span>
              <input value={postnom} onChange={(e) => setPostnom(e.target.value)} className="w-full rounded-lg border border-outline-variant/25 bg-white px-3 py-2" />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-on-surface-variant">Sexe</span>
              <AcademiaSelect value={sexe} onChange={(e) => setSexe(e.target.value)}>
                <option value="">Non renseigné</option>
                <option value="M">Masculin</option>
                <option value="F">Féminin</option>
              </AcademiaSelect>
            </label>
            <label className="text-sm md:col-span-2">
              <span className="mb-1 block text-on-surface-variant">Date de naissance</span>
              <input type="date" value={dateNaissance} onChange={(e) => setDateNaissance(e.target.value)} className="w-full rounded-lg border border-outline-variant/25 bg-white px-3 py-2 md:max-w-sm" />
            </label>
          </div>

          <div className="mt-6">
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={isSaving || isUploading}
              className="rounded-lg bg-primary px-5 py-2.5 text-sm font-black text-white transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSaving ? 'Enregistrement…' : 'Enregistrer le profil'}
            </button>
          </div>
        </article>
      </div>

      <article className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-sm">
        <h2 className="text-sm font-black uppercase tracking-wider text-on-surface-variant">Sécurité du compte</h2>
        <p className="mt-1 text-sm text-on-surface-variant">
          Modifiez votre mot de passe administrateur.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
          <label className="text-sm">
            <span className="mb-1 block text-on-surface-variant">Mot de passe actuel</span>
            <input
              type="password"
              value={ancienMotdepasse}
              onChange={(e) => setAncienMotdepasse(e.target.value)}
              className="w-full rounded-lg border border-outline-variant/25 bg-white px-3 py-2"
              autoComplete="current-password"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-on-surface-variant">Nouveau mot de passe</span>
            <input
              type="password"
              value={nouveauMotdepasse}
              onChange={(e) => setNouveauMotdepasse(e.target.value)}
              className="w-full rounded-lg border border-outline-variant/25 bg-white px-3 py-2"
              autoComplete="new-password"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-on-surface-variant">Confirmer le nouveau mot de passe</span>
            <input
              type="password"
              value={confirmMotdepasse}
              onChange={(e) => setConfirmMotdepasse(e.target.value)}
              className="w-full rounded-lg border border-outline-variant/25 bg-white px-3 py-2"
              autoComplete="new-password"
            />
          </label>
        </div>
        <div className="mt-6">
          <button
            type="button"
            onClick={() => void handleChangePassword()}
            disabled={isChangingPassword}
            className="rounded-lg bg-primary px-5 py-2.5 text-sm font-black text-white transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isChangingPassword ? 'Mise à jour…' : 'Changer le mot de passe'}
          </button>
        </div>
      </article>
    </>
  );

  return (
    <PageShell
      title={isApprenant ? 'Mon profil' : 'Profil administrateur'}
      description={
        isApprenant
          ? 'Consultez et mettez à jour vos informations personnelles.'
          : 'Consultez et mettez à jour vos informations personnelles.'
      }
      sideCard={{
        label: isApprenant ? 'Mon compte' : 'Compte admin',
        value: initials,
        helper: displayName,
        sideLinks: isApprenant
          ? [
              { label: 'Accueil', to: '/app', icon: 'dashboard' },
              { label: 'Mon dossier', to: '/app/documents', icon: 'folder' },
            ]
          : [{ label: 'Tableau de bord', to: '/admin/dashboard', icon: 'dashboard' }],
      }}
    >
      <AdminToast
        message={toast?.message ?? null}
        variant={toast?.variant ?? 'success'}
        onClose={dismissToast}
      />
      {isApprenant ? apprenantContent : adminContent}
    </PageShell>
  );
}
